import Main from "niteroi/packages/sales-distribution/controllers/Main.controller";
import {SearchField$LiveChangeEvent} from "sap/m/SearchField";
import {Table$RowSelectionChangeEvent} from "sap/ui/table/Table";
import {
    IBusinessPartner,
    IQuotation,
    IQuotationItem,
} from "niteroi/packages/sales-distribution/models/interfaces";
import JSONModel from "sap/ui/model/json/JSONModel";
import {Route$PatternMatchedEvent} from "sap/ui/core/routing/Route";
import {ListBase$DeleteEvent} from "sap/m/ListBase";
import Input from "sap/m/Input";
import {ILoadDataResponse} from "niteroi/packages/system/models/interfaces";
import Dialog from "sap/m/Dialog";
import {InputBase$ChangeEvent} from "sap/m/InputBase";

export default class Quotations extends Main {
    private readonly onPatternMatchedHandler = this.onPatternMatched.bind(this);

    public onInit(): void {
        super.onInit();
        const router = this.getRouter();
        const routeName = this.getCurrentRouteName(router);
        if (routeName) {
            const route = router.getRoute(routeName);
            route.detachPatternMatched(this.onPatternMatchedHandler);
            route.attachPatternMatched(this.onPatternMatchedHandler);
        }
    }

    public onBeforeRendering(): void {
        super.onBeforeRendering();
    }

    protected onPatternMatched(event: Route$PatternMatchedEvent): void {
        const routeName = event.getParameter("name");
        if (routeName === "sales-distribution/quotations/add") {
            this.getModel("salesDistributionQuotationDetail").setData(this.getBlankDocumentModel());
            this.getView().setBusy(false);
        } else {
            const id = (event.getParameter("arguments") as { id?: string })?.id ?? "";
            this.getData(id);
        }

        const model = this.getModel("salesDistributionBusinessPartnerCollection");
        const url = "/api/sales-distribution/business-partners";
        const headers = this.getRequestHeaders();
        model.loadData(url, "", true, "GET", false, true, headers).then((): null => null);
    }

    protected getData(objectId: string = ""): void {
        const componentRoute = "/api/sales-distribution/quotations";
        const url = objectId ? `${componentRoute}/${objectId}` : componentRoute;
        const headers = this.getRequestHeaders();
        const modelName = objectId ? "salesDistributionQuotationDetail" : "salesDistributionQuotationCollection";
        const model = this.getModel(modelName);
        model.loadData(url, "", true, "GET", false, true, headers).then(() => {
            if (objectId) {
                this.getModel("salesDistributionBusinessPartnerDetail").setData(model.getProperty("/businessPartner"));
            }
            model.refresh(true);
        }).catch((response: ILoadDataResponse) => {
            const responseText = JSON.parse(response.responseText);
            this.showToast(`${responseText.code} - ${responseText.error}`);
        }).finally(() => {
            this.getView().setBusy(false);
        });
    }

    public onListSearch(event: SearchField$LiveChangeEvent): void {
        this.onSearchList(event, "idQuotationsTable", [
            "quotationNumber",
            "quotationId",
            "status",
            "versionNumber",
            "documentDate",
            "validToDate",
            "bindingDate",
            "currency",
            "netValue",
            "soldToPartnerId",
            "createdAt"
        ]);
    }

    protected getBlankDocumentModel(): IQuotation {
        return {
            quotationId: "",
            quotationNumber: "",
            versionNumber: 1,
            status: "DRAFT",
            documentDate: "",
            validToDate: "",
            bindingDate: null,
            currency: "EUR",
            netValue: 0.00,
            createdAt: "",
            updatedAt: "",
            soldToPartnerId: "",
            businessPartner: null,
            inquiryId: null,
            items: []
        };
    }

    protected getBlankQuotationPartner(): IBusinessPartner {
        return {
            businessPartnerId: "",
            customerNumber: "",
            legalName1: "",
            legalName2: "",
            vatId: "",
            taxNumber: "",
            partnerRole: "",
            isActive: false,
            createdAt: "",
            updatedAt: "",
            companyId: "",
            addresses: [],
            salesAreas: []
        };
    }

    public onRowSelectionChange(event: Table$RowSelectionChangeEvent): void {
        const rowContext = event.getParameter("rowContext");
        const selectedDocument = rowContext?.getObject() as IQuotation | undefined;

        if (!selectedDocument?.quotationId) {
            return;
        }

        if (selectedDocument.soldToPartnerId && selectedDocument.businessPartner) {
            this.getModel("salesDistributionBusinessPartnerDetail").setData(selectedDocument.businessPartner);
        }

        this.getModel("salesDistributionQuotationDetail").setData(selectedDocument);
        this.navTo("sales-distribution/quotations/edit", {id: selectedDocument.quotationId}, false);
    }

    public onNavButtonPress(): void {
        this.navTo("sales-distribution/quotations", {}, false);
    }

    public onCreateQuotationPress(): void {
        this.getModel("salesDistributionQuotationDetail").setData(this.getBlankDocumentModel());
        this.getModel("salesDistributionBusinessPartnerDetail").setData(this.getBlankQuotationPartner());
        this.navTo("sales-distribution/quotations/add", {}, true);
    }

    public onRefreshQuotationsPress(): void {
        this.getModel("salesDistributionQuotationCollection")?.refresh(true);
    }

    public onPressSaveDraft(): void {
        this.getModel("salesDistributionQuotationDetail").setProperty("/status", "DRAFT");
        this.persist();
    }

    public onPressViewDocument(): void {
        const model = this.getModel("salesDistributionQuotationDetail");
        const status = model.getProperty("/status");
        const quotationId = model.getProperty("/quotationId");
        const quotationNumber = model.getProperty("/quotationNumber");
        if (status === "DRAFT" || status === "REJECTED" || !quotationId || !quotationNumber) {
            return;
        }

        const dialogModel = new JSONModel({
            "documentSrc": `/api/sales-distribution/quotations/${quotationId}/print`,
            "fileName": `${quotationNumber}.pdf`,
            "mimeType": "application/pdf"
        });

        this.getView().setModel(dialogModel, "dialogModel");

        (this.byId("niteroiDocumentPrintDialog") as Dialog).open();
    }

    public onDocumentsDialogAfterClose(): void {
        this.getView().setModel(new JSONModel({}), "dialogModel");
    }

    public onPressSendQuotation(): void {
        const model = this.getModel("salesDistributionQuotationDetail");
        const businessPartner = model.getProperty("/businessPartner") as IBusinessPartner;
        const partnerAddress = (businessPartner.addresses).find((address) => address.addressType === "BILLING");
        if (!partnerAddress) {
            const confirmationDialog = this.byId("niteroiConfirmationDialog") as Dialog;
            const dialogModel = this.getModel("confirmationDialog");
            dialogModel.setData({
                type: "Message",
                title: "Do you want to continue?",
                content: "This business partner does not have a valid billing address with email. If you wish to continue, this action will update the status of the document without sending it via email. You can still view the document as PDF and download it.",
                submitText: "Continue",
                continueText: "",
                cancelText: "Cancel"
            });
            confirmationDialog.open();
            return;
        }
        model.setProperty("/status", "SENT");
        this.persist();
    }

    public onPressDialogSubmitButton(): void {
        const model = this.getModel("salesDistributionQuotationDetail");
        model.setProperty("/status", "SENT");
        this.persist();
        const confirmationDialog = this.byId("niteroiConfirmationDialog") as Dialog;
        confirmationDialog.close();
    }

    public onPressDialogCancelButton(): void {
        const confirmationDialog = this.byId("niteroiConfirmationDialog") as Dialog;
        confirmationDialog.close();
    }

    public onPressDialogContinueButton(): void {
        const confirmationDialog = this.byId("niteroiConfirmationDialog") as Dialog;
        confirmationDialog.close();
    }

    public onPressAcceptQuotation(): void {
        this.getModel("salesDistributionQuotationDetail").setProperty("/status", "ACCEPTED");
        this.getModel("salesDistributionQuotationDetail").setProperty("/bindingDate", new Date().toISOString().split("T")[0]);
        this.persist();
    }

    public onPressRejectQuotation(): void {
        this.getModel("salesDistributionQuotationDetail").setProperty("/status", "REJECTED");
        this.persist();
    }

    public onPressSaveDocument(): void {
        const quotationModel = this.getModel("salesDistributionQuotationDetail");

        const items = quotationModel.getProperty("/items") as Array<IQuotationItem>;
        if (!items || items.length === 0) {
            return;
        }
        const invalidItems = items.filter((item) => !item.description || !item.unitPrice || !item.quantity || !item.materialNumber || !item.taxCode || !item.unit || !item.requestedDate);
        if (invalidItems.length > 0) {
            this.showToast(this.getText("salesDistributionQuotationSavingNotAllowed"));
            return;
        }

        const soldToPartnerId = quotationModel.getProperty("/soldToPartnerId");
        if (!soldToPartnerId) {
            return;
        }

        quotationModel.setProperty("/status", "OPEN");
        const now = new Date().toISOString().replace("T", " ").split(".")[0];
        if (!quotationModel.getProperty("/documentDate")) {
            quotationModel.setProperty("/documentDate", now.split(" ")[0]);
        }
        if (!quotationModel.getProperty("/createdAt")) {
            quotationModel.setProperty("/createdAt", now);
        }
        quotationModel.setProperty("/updatedAt", now);

        this.persist();
    }

    private persist(): void {
        this.getView().setBusy(true);
        const quotationModel = this.getModel("salesDistributionQuotationDetail");
        const quotation = quotationModel.getData() as IQuotation;

        if (!quotation.soldToPartnerId) {
            this.getView().setBusy(false);
            return;
        }
        const componentRoute = "/api/sales-distribution/quotations";
        const url = quotation.quotationId ? `${componentRoute}/${quotation.quotationId}` : componentRoute;
        const method = quotation.quotationId ? "PUT" : "POST";
        quotationModel.loadData(url, JSON.stringify(quotation), true, method, false, true, this.getRequestHeaders()).then(() => {
            if (method === "POST") {
                this.navTo("sales-distribution/quotations/edit", { id: quotationModel.getProperty("/quotationId") }, false);
            }
        }).catch((response: ILoadDataResponse) => {
            const responseText = JSON.parse(response.responseText);
            this.showToast(`${responseText.code} - ${responseText.error}`);
        }).finally(() => {
            this.getView().setBusy(false);
        });
    }

    public onPressAddItem(): void {
        const model = this.getModel("salesDistributionQuotationDetail");
        const modelData = model.getData() as IQuotation;
        const lastIndex = modelData.items.slice(-1);
        const nextItemNumber = lastIndex[0] ? (lastIndex[0].itemNumber + 10) : 10;
        const newItem: IQuotationItem = {
            quotationItemId: "",
            quotationId: modelData.quotationId,
            itemNumber: nextItemNumber,
            materialNumber: "",
            description: "",
            quantity: 0,
            unit: "PCE",
            unitPrice: 0,
            taxCode: "A1",
            requestedDate: null
        };
        modelData.items.push(newItem);
        model.refresh(true);
    }

    public onDeleteItem(event: ListBase$DeleteEvent): void {
        const source = event.getParameter("listItem");
        const bindingContext = source.getBindingContext("salesDistributionQuotationDetail");
        const model = bindingContext.getModel() as JSONModel;
        const quotationItemId = model.getProperty(bindingContext.getPath()).quotationItemId;
        const itemIndex = Number(bindingContext.getPath().split("/").pop());
        const modelData = model.getData() as IQuotation;
        const resolvedIndex = quotationItemId
            ? modelData.items.findIndex((item) => item.quotationItemId === quotationItemId)
            : itemIndex;
        if (resolvedIndex > -1) {
            modelData.items.splice(resolvedIndex, 1);
            model.refresh(true);
            this.onChangeLineItem();
        }
    }

    public onChangeLineItem(): void {
        const model = this.getModel("salesDistributionQuotationDetail");
        const modelData = model.getData() as IQuotation;
        let netValue = 0.00;
        modelData.items.forEach((item) => {
            if (item.unitPrice && item.quantity) {
                const itemNetValue = Number(item.unitPrice) * Number(item.quantity);
                netValue += Math.round((itemNetValue + Number.EPSILON) * 100) / 100;
            }
        });
        model.setProperty("/netValue", netValue.toFixed(2));
    }

    /**
     * Internal helper to update the quotation model state.
     */
    protected _updatePartner(partner: IBusinessPartner): void {
        const quotationModel = this.getView()?.getModel("salesDistributionQuotationDetail") as JSONModel;
        if (quotationModel) {
            quotationModel.setProperty("/soldToPartnerId", partner.businessPartnerId);
            this.getModel("salesDistributionBusinessPartnerDetail").setData(partner);
        }
    }

    public onPressBusinessPartnerLegalName(): void {
        (this.byId("soldToPartnerInput") as Input).fireValueHelpRequest();
    }

    public onChangeBusinessPartnerInput(event: InputBase$ChangeEvent): void {
        const source = event.getSource();
        source.setValueState("None");
        const value = source.getValue();
        if (!value) {
            return;
        }
        const businessPartnersModel = this.getModel("salesDistributionBusinessPartnerCollection");
        const businessPartners = businessPartnersModel.getData() as Array<IBusinessPartner>;
        if (!businessPartners.find((partner) => partner.businessPartnerId === value)) {
            source.setValueState("Error");
            this.getModel("salesDistributionQuotationDetail").setProperty("/soldToPartnerId", "");
            source.setValue("");
        }
    }
}

